import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LogEntry, StreamFrame, World } from '../../types';
import { errorMessage, Worlds } from '../../api/client';
import { StreamStatus, useWorldStream } from '../../hooks/useWorldStream';
import { Icon } from '../ui/Icon';
import { Loading, Note } from '../ui/primitives';
import { logLevel } from '../../utils/format';
import { downloadLogs, fmtLogTs } from '../../utils/logs';

/** Lines kept in the browser; older ones scroll off the top. */
const MAX_LINES = 5000;
/** How close to the bottom (px) still counts as "following". */
const FOLLOW_SLACK = 8;

type Row = LogEntry & { divider?: boolean };
type LevelFilter = 'all' | 'warn' | 'err';

const STATUS_LABEL: Record<StreamStatus, string> = {
  off: 'Off',
  connecting: 'Connecting to the box…',
  open: 'Live',
  reconnecting: 'Reconnecting…',
  paused: 'Paused (tab hidden)',
  ended: 'Ended',
  error: 'Stopped',
};

const END_LABEL: Record<string, string> = {
  container_exited: 'the container exited',
  ssh_closed: 'the connection to the box dropped',
  server_shutdown: 'the control service closed the stream',
};

const dotClass = (status: StreamStatus) =>
  status === 'open' ? 'ldot ldot--on' : status === 'connecting' || status === 'reconnecting' ? 'ldot ldot--busy' : status === 'error' ? 'ldot ldot--err' : 'ldot';

interface Props {
  world: World;
  /** Developers only: show the console command box under the log. */
  consoleAllowed?: boolean;
}

/** `docker logs -f` of the world's container, streamed over the WebSocket. Oldest first, follows the tail. */
const LiveLogs: React.FC<Props> = ({ world, consoleAllowed }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const bufRef = useRef<Row[]>([]);
  const frameRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const [follow, setFollow] = useState(true);
  const [level, setLevel] = useState<LevelFilter>('all');
  const [query, setQuery] = useState('');
  const termRef = useRef<HTMLDivElement>(null);
  const [cmd, setCmd] = useState('');
  const [sending, setSending] = useState(false);
  const [cmdError, setCmdError] = useState<string | null>(null);

  const render = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = true;
    window.requestAnimationFrame(() => {
      frameRef.current = false;
      setRows([...bufRef.current]);
    });
  }, []);

  const onFrame = useCallback(
    (frame: StreamFrame) => {
      if (frame.type !== 'log') return;
      const buf = bufRef.current;
      if (frame.replay && buf.length) {
        buf.push({ timestamp: Date.now(), message: 'reconnected · lines in between may be missing', divider: true });
      }
      frame.lines.forEach((l) => buf.push({ timestamp: l.ts, message: l.m }));
      if (buf.length > MAX_LINES) buf.splice(0, buf.length - MAX_LINES);
      if (!pausedRef.current) render();
    },
    [render]
  );

  const { status, error, endReason, retry } = useWorldStream(world.id, 'logs', true, onFrame);

  // A different world starts from an empty console.
  useEffect(() => {
    bufRef.current = [];
    setRows([]);
    setPaused(false);
    setFollow(true);
    setCmd('');
    setCmdError(null);
  }, [world.id]);

  // Follow the tail unless the reader scrolled up.
  useEffect(() => {
    const el = termRef.current;
    if (el && follow) el.scrollTop = el.scrollHeight;
  }, [rows, follow]);

  const onScroll = () => {
    const el = termRef.current;
    if (!el) return;
    setFollow(el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK);
  };

  const resume = () => {
    setPaused(false);
    render();
  };

  const clear = () => {
    bufRef.current = [];
    setRows([]);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (r.divider) return true;
      if (level !== 'all') {
        const lv = logLevel(r.message);
        if (level === 'err' && lv !== 'err') return false;
        if (level === 'warn' && lv !== 'err' && lv !== 'warn') return false;
      }
      return !q || r.message.toLowerCase().includes(q);
    });
  }, [rows, level, query]);

  const exportable = rows.filter((r) => !r.divider);
  const finished = status === 'ended' || status === 'error';

  // Developer console: the command runs on the server as the console sender, so its output arrives in the stream.
  const typed = cmd.trim().replace(/^\//, '').trim();
  const canSend = status === 'open' && !sending;
  const send = async () => {
    if (!typed || !canSend) return;
    setSending(true);
    setCmdError(null);
    try {
      await Worlds.consoleCommand(world.id, typed);
      bufRef.current.push({ timestamp: Date.now(), message: `sent: /${typed}`, divider: true });
      resume();
      setFollow(true);
      setCmd('');
    } catch (e) {
      setCmdError(errorMessage(e, 'Could not send the command.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="term-bar">
        <span className="chip" title={error || undefined}>
          <span className={dotClass(status)} />
          {status === 'ended' && endReason ? `Ended: ${END_LABEL[endReason] || endReason}` : STATUS_LABEL[status]}
        </span>
        <div className="seg" role="group" aria-label="Level">
          {(['all', 'warn', 'err'] as LevelFilter[]).map((l) => (
            <button key={l} type="button" aria-pressed={level === l} onClick={() => setLevel(l)}>
              {l === 'all' ? 'All' : l === 'warn' ? 'Warn+' : 'Errors'}
            </button>
          ))}
        </div>
        <input className="input input--mono" placeholder="Filter lines…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ flex: '1 1 160px' }} />
        {finished ? (
          <button type="button" className="btn btn--sm" onClick={retry}>
            <Icon name="refresh" size={12} /> Reconnect
          </button>
        ) : paused ? (
          <button type="button" className="btn btn--sm" onClick={resume}>
            <Icon name="play" size={12} /> Resume
          </button>
        ) : (
          <button type="button" className="btn btn--sm" onClick={() => setPaused(true)}>
            <Icon name="pause" size={12} /> Pause
          </button>
        )}
        <button type="button" className="btn btn--sm btn--ghost" onClick={clear} disabled={rows.length === 0}>
          <Icon name="x" size={12} /> Clear
        </button>
        <button type="button" className="btn btn--sm" onClick={() => downloadLogs(world, exportable, 'live')} disabled={exportable.length === 0}>
          <Icon name="download" size={12} /> Export .txt
        </button>
      </div>
      {error && status !== 'open' && <Note tone={status === 'error' ? 'danger' : 'warn'}>{error}</Note>}
      {rows.length === 0 && !finished ? (
        <Loading label={status === 'open' ? 'Waiting for output…' : STATUS_LABEL[status]} />
      ) : (
        <div className="term-wrap">
          <div className="term term--live" ref={termRef} onScroll={onScroll}>
            {visible.map((r, i) =>
              r.divider ? (
                <div className="term-div" key={`d-${r.timestamp}-${i}`}>
                  {r.message}
                </div>
              ) : (
                <div className="term-row" key={`${r.timestamp}-${i}`}>
                  <span className="t">{fmtLogTs(r.timestamp)}</span>
                  <span className={`lv-${logLevel(r.message)}`}>{logLevel(r.message) === 'err' ? 'ERROR' : logLevel(r.message).toUpperCase()}</span>
                  <span>{r.message}</span>
                </div>
              )
            )}
            {visible.length === 0 && <div className="term-div">no lines match the filter</div>}
          </div>
          {!follow && (
            <button type="button" className="btn btn--sm btn--primary term-jump" onClick={() => setFollow(true)}>
              <Icon name="chevron" size={12} /> Jump to latest
            </button>
          )}
        </div>
      )}
      {consoleAllowed && (
        <>
          <div className="term-cmd">
            <input
              className="input input--mono"
              placeholder="Console command… (Enter to run)"
              aria-label="Console command"
              value={cmd}
              onChange={(e) => {
                setCmd(e.target.value);
                setCmdError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              disabled={!canSend}
              maxLength={256}
              autoComplete="off"
              spellCheck={false}
            />
            <button type="button" className="btn btn--sm btn--primary" onClick={send} disabled={!canSend || !typed}>
              {sending ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="play" size={12} />} Run
            </button>
          </div>
          {cmdError && (
            <div style={{ marginTop: 12 }}>
              <Note tone="danger">{cmdError}</Note>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default LiveLogs;
